/**
 * Collapses cascade property_updated events into their source events.
 *
 * When a property_updated event has source_type_name === "ahoy_timeline_event",
 * its source_id points to the event_id of the triggering event (e.g., a formula
 * recomputation after a user edit). These cascade events are absorbed into the
 * source event's TimelineItem so they render as one combined entry.
 *
 * Also assigns PROPERTY_UPDATED and OBJECT_CREATED item types so these events
 * are routed to their dedicated item components (not SingleEventItem).
 */

import type { TimelineItem } from './collapseAssociationEvents';
import { ITEM_TYPES } from './collapseAssociationEvents';
import type { TimelineEvent } from './types';
import { EVENT_TYPES } from './types';

/**
 * Assigns dedicated item types for property_updated and object_created events,
 * then absorbs cascade events into their source event's item.
 */
export function collapseCascadeEvents(items: TimelineItem[]): TimelineItem[] {
  // Step 1: Assign dedicated item types for property/created events
  const typed = items.map((item): TimelineItem => {
    if (item.type !== ITEM_TYPES.SINGLE_EVENT || item.events.length !== 1) {
      return item;
    }
    const event = item.events[0];
    if (event.event_type_name === EVENT_TYPES.PROPERTY_UPDATED) {
      return { type: ITEM_TYPES.PROPERTY_UPDATED, events: item.events };
    }
    if (event.event_type_name === EVENT_TYPES.OBJECT_CREATED) {
      return { type: ITEM_TYPES.OBJECT_CREATED, events: item.events };
    }
    return item;
  });

  // Step 2: Build event_id → item index map
  // Normalize to string — event_id may be a number from JSON, source_id is always a string
  const eventIdToIdx = new Map<string, number>();
  for (let i = 0; i < typed.length; i++) {
    for (const event of typed[i].events) {
      eventIdToIdx.set(String(event.event_id), i);
    }
  }

  // Step 3: For each cascade event, find the root source (follow chain up)
  // Stops at entity boundaries — cross-entity cascades should not be absorbed.
  function findRootIdx(
    sourceId: string,
    visited: Set<string>,
    entityId: string
  ): number | null {
    const key = String(sourceId);
    if (visited.has(key)) {
      return null;
    } // cycle protection
    visited.add(key);

    const idx = eventIdToIdx.get(key);
    if (idx === undefined) {
      return null;
    }

    const item = typed[idx];

    // Stop at entity boundary — don't absorb cross-entity cascades
    if (item.events[0]?.entity_id !== entityId) {
      return null;
    }

    if (item.events.length !== 1) {
      return idx;
    } // already a group, stop here

    const event = item.events[0];
    // If source is also a cascade, follow up the chain
    if (
      event.event_type_name === EVENT_TYPES.PROPERTY_UPDATED &&
      event.source_type_name === 'ahoy_timeline_event' &&
      event.source_id
    ) {
      const parentIdx = findRootIdx(event.source_id, visited, entityId);
      return parentIdx !== null ? parentIdx : idx;
    }

    return idx;
  }

  // Step 4: Identify cascade events and map them to their root source
  const absorbed = new Set<number>(); // item indices to remove
  const cascadesByRoot = new Map<number, TimelineEvent[]>();
  const orphanedCascades: number[] = []; // cascade items that couldn't find a renderable root

  for (let i = 0; i < typed.length; i++) {
    const item = typed[i];
    if (item.events.length !== 1) {
      continue;
    }

    const event = item.events[0];
    if (
      event.event_type_name === EVENT_TYPES.PROPERTY_UPDATED &&
      event.source_type_name === 'ahoy_timeline_event' &&
      event.source_id
    ) {
      const rootIdx = findRootIdx(
        event.source_id,
        new Set([String(event.event_id)]),
        event.entity_id
      );
      if (rootIdx !== null && rootIdx !== i) {
        const rootItem = typed[rootIdx];
        // Only absorb into items that can render property updates
        if (
          rootItem.type === ITEM_TYPES.PROPERTY_UPDATED ||
          rootItem.type === ITEM_TYPES.OBJECT_CREATED
        ) {
          absorbed.add(i);
          const existing = cascadesByRoot.get(rootIdx) || [];
          existing.push(event);
          cascadesByRoot.set(rootIdx, existing);
        } else {
          // Root is non-renderable (e.g., association item) — try fallback
          orphanedCascades.push(i);
        }
      }
    }
  }

  // Step 4b: Fallback for orphaned cascades — absorb into the nearest
  // same-entity PROPERTY_UPDATED or OBJECT_CREATED item.
  // This handles cases where the source_id chain goes through an association
  // event (e.g., backfill formula cascades triggered by associations).
  const orphanSet = new Set(orphanedCascades);
  for (const i of orphanedCascades) {
    const event = typed[i].events[0];
    let bestIdx: number | null = null;
    let bestDist = Infinity;

    for (let j = 0; j < typed.length; j++) {
      if (j === i || absorbed.has(j) || orphanSet.has(j)) {
        continue;
      }
      const candidate = typed[j];
      if (
        candidate.events[0]?.entity_id === event.entity_id &&
        (candidate.type === ITEM_TYPES.PROPERTY_UPDATED ||
          candidate.type === ITEM_TYPES.OBJECT_CREATED)
      ) {
        const dist = Math.abs(j - i);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = j;
        }
      }
    }

    if (bestIdx !== null) {
      absorbed.add(i);
      const existing = cascadesByRoot.get(bestIdx) || [];
      existing.push(event);
      cascadesByRoot.set(bestIdx, existing);
    }
  }

  // Step 5: Build result — merge cascades into root items, skip absorbed items
  const result: TimelineItem[] = [];
  for (let i = 0; i < typed.length; i++) {
    if (absorbed.has(i)) {
      continue;
    }

    const item = typed[i];
    const cascades = cascadesByRoot.get(i);
    if (cascades) {
      result.push({
        type: item.type,
        events: [...item.events, ...cascades],
      });
    } else {
      result.push(item);
    }
  }

  return result;
}
